import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePickerField } from "@/components/shared/DatePickerField";
import { TimePickerField } from "@/components/shared/TimePickerField";
import { parseLocalDateOnly, todayInUruguay } from "@/lib/dateFormat";

export interface ScheduleSendDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-filled date (YYYY-MM-DD) and time (HH:mm), e.g. when rescheduling. */
  initialDate?: string;
  initialTime?: string;
  onConfirm: (sendAtIso: string) => void | Promise<void>;
  submitting?: boolean;
  /** True when reopening this dialog to change an already-scheduled send's date/time. */
  isReschedule?: boolean;
}

/**
 * Uruguay does not observe DST (abolished 2015) — its UTC offset is a fixed
 * -03:00, so a wall-clock date+time picked in this dialog converts to a
 * real instant with a plain fixed-offset ISO suffix, no timezone library
 * needed.
 */
function toUruguayInstantIso(date: string, time: string): string {
  return `${date}T${time}:00-03:00`;
}

/** Reused by both the invoice-creation "Schedule send" flow and the invoice-detail "Reschedule" action. */
export function ScheduleSendDialog({
  open,
  onOpenChange,
  initialDate,
  initialTime,
  onConfirm,
  submitting,
  isReschedule,
}: ScheduleSendDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [date, setDate] = useState(initialDate || todayInUruguay());
  const [time, setTime] = useState(initialTime || "09:00");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDate(initialDate || todayInUruguay());
      setTime(initialTime || "09:00");
      setError(null);
    }
  }, [open, initialDate, initialTime]);

  const handleConfirm = async (): Promise<void> => {
    if (!date || !/^\d{2}:\d{2}$/.test(time)) {
      setError(t("invoices.schedule.invalidDateTime", "Elegí una fecha y hora válidas."));
      return;
    }
    const sendAtIso = toUruguayInstantIso(date, time);
    if (new Date(sendAtIso).getTime() <= Date.now()) {
      setError(t("invoices.schedule.mustBeFuture", "La fecha y hora deben ser en el futuro."));
      return;
    }
    setError(null);
    await onConfirm(sendAtIso);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {isReschedule
              ? t("invoices.schedule.rescheduleTitle", "Reprogramar envío")
              : t("invoices.schedule.title", "Programar envío")}
          </DialogTitle>
          <DialogDescription>
            {isReschedule
              ? t(
                  "invoices.schedule.rescheduleDescription",
                  "Elegí la nueva fecha y hora en la que se emitirá el comprobante."
                )
              : t(
                  "invoices.schedule.description",
                  "El comprobante se emitirá automáticamente en la fecha y hora elegidas."
                )}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <DatePickerField
            id="schedule-send-date"
            label={t("invoices.schedule.dateLabel", "Fecha")}
            value={date}
            onChange={setDate}
            minDate={parseLocalDateOnly(todayInUruguay())}
          />
          <TimePickerField
            id="schedule-send-time"
            label={t("invoices.schedule.timeLabel", "Hora")}
            value={time}
            onChange={setTime}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {t("invoices.schedule.cancel", "Cancelar")}
          </Button>
          <Button type="button" onClick={() => void handleConfirm()} disabled={submitting}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isReschedule
                  ? t("invoices.schedule.rescheduling", "Reprogramando...")
                  : t("invoices.schedule.scheduling", "Programando...")}
              </>
            ) : isReschedule ? (
              t("invoices.schedule.rescheduleConfirm", "Reprogramar")
            ) : (
              t("invoices.schedule.confirm", "Programar")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
